import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getInfrastructureMetrics;

export type DbInstanceInfraMetricsQueryData = {
  items: ApiResponse<typeof apiFn>;
  range: TimeRange;
  stepSeconds: number;
};

type DbInstanceInfraMetricsQueryArgs<SELECT> = QueryOptions<
  DbInstanceInfraMetricsQueryData,
  SELECT
> & {
  key: string;
  instanceId?: string;
  stepSeconds?: number;
  getRange: () => TimeRange;
};

const DBO_INSTANCE_INFRA_METRICS_QUERY_KEY = 'dbo-instance-infra-metrics';

const getDbInstanceInfraMetricsQueryKey = ({
  key,
  instanceId,
  stepSeconds,
}: { key?: string; instanceId?: string; stepSeconds?: number } = {}) =>
  [DBO_INSTANCE_INFRA_METRICS_QUERY_KEY, key, instanceId, stepSeconds] as const;

const getDbInstanceInfraMetricsQueryOptions = ({
  key,
  instanceId,
  stepSeconds = 60,
  getRange,
  enabled,
}: {
  key: string;
  instanceId?: string;
  stepSeconds?: number;
  getRange: () => TimeRange;
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getDbInstanceInfraMetricsQueryKey({
      key,
      instanceId,
      stepSeconds,
    }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        instanceId: instanceId!,
        startTime: range.from,
        endTime: range.to,
        stepSeconds,
      });
      return { items: data, range, stepSeconds };
    },
    enabled: makeIsEnabled(!!instanceId, enabled),
  });

export const useDbInstanceInfraMetricsQuery = <
  SELECT = DbInstanceInfraMetricsQueryData
>({
  key,
  instanceId,
  stepSeconds,
  getRange,
  enabled,
  select,
  refetchInterval,
}: DbInstanceInfraMetricsQueryArgs<SELECT>) => {
  return useQuery({
    ...getDbInstanceInfraMetricsQueryOptions({
      key,
      instanceId,
      stepSeconds,
      getRange,
      enabled,
    }),
    select,
    refetchInterval,
  });
};
